import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {useBusiness} from '../contexts/BusinessContext';
import {allowsStripeBilling, isIosConsumptionOnly} from '../lib/billing/billingChannel';
import {cardFreeTrialRemainingLabel, getCardFreeTrialStatus} from '../lib/billing/cardFreeTrialService';
import {formatBillingDate} from '../lib/billing/billingDates';

export default function CardFreeTrialNotice() {
  const {activeBusiness, activeAccess, userId} = useBusiness();
  const businessId = activeBusiness?.id;
  const statusQuery = useQuery({
    queryKey: ['card-free-trial-status', userId, businessId],
    queryFn: () => getCardFreeTrialStatus(businessId),
    enabled: Boolean(userId && businessId && activeAccess?.mode !== 'accountant'),
    refetchOnWindowFocus: true,
    staleTime: 60000
  });

  const status = statusQuery.data;
  if (!status) return null;

  const iosConsumptionOnly = isIosConsumptionOnly();
  const billingAllowed = activeAccess?.mode === 'owner' && allowsStripeBilling();
  const expired = status.access_state === 'expired';

  return (
    <div
      className={`border-b px-4 py-3 text-sm sm:px-6 lg:px-8 ${expired ? 'border-amber-200 bg-amber-50 text-amber-950' : 'border-teal-200 bg-mint text-primary'}`}
      role="status"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">
            {expired ? 'Plan access is read-only' : iosConsumptionOnly ? 'Solo plan access is active' : 'Your card-free Solo trial is active'}
          </p>
          <p className="mt-0.5 leading-5">
            {expired
              ? 'Existing records remain available to view and export. Record changes are not available under your current plan.'
              : iosConsumptionOnly
              ? `Plan access is available until ${formatBillingDate(status.trial_ends_at)}.`
              : `${cardFreeTrialRemainingLabel(status)} · ends ${formatBillingDate(status.trial_ends_at)}. No payment method is on file and no automatic charge will occur.`}
          </p>
        </div>
        {billingAllowed && (
          <Link className="min-h-10 rounded-lg px-3 py-2 font-semibold underline underline-offset-2" to="/settings/billing">
            {expired ? 'Review plan options' : 'Billing and plan options'}
          </Link>
        )}
      </div>
    </div>
  );
}
